/* eslint-disable prettier/prettier */
import React, { useState } from 'react';
import { Text, IconButton, Tooltip, useToast } from '@chakra-ui/react';
import { TbPercentage } from 'react-icons/tb';
import { FileSpreadsheet } from 'lucide-react';
import { ReturnButton } from '../../../../../components/buttons/ReturnButton';
import { ITaxAnalysisFilters } from '../../../../../types/taxAnalysis';

interface TaxHeaderSNProps {
  title: string;
  subtitle: string;
  iconBgColor?: string;
  iconColor?: string;
  filters?: ITaxAnalysisFilters;
  // eslint-disable-next-line no-unused-vars
  onExportCSV?: (filters: ITaxAnalysisFilters) => Promise<any>;
}

export function TaxHeaderSN({
  title,
  subtitle,
  iconBgColor = '#FFF8E1',
  iconColor = '#FFA000',
  filters,
  onExportCSV,
}: TaxHeaderSNProps) {
  const [isExporting, setIsExporting] = useState(false);
  const toast = useToast();

  const handleExportCSV = async () => {
    if (!onExportCSV || !filters) return;

    setIsExporting(true);
    try {
      const data = await onExportCSV(filters);

      if ('error' in data) {
        toast({
          title: 'Erro ao exportar',
          description: data.message || data.error,
          status: 'error',
          duration: 5000,
          isClosable: true,
        });
        return;
      }

      // Converter dados para CSV
      if (!data || data.length === 0) {
        toast({
          title: 'Nenhum dado para exportar',
          description: 'Não há empresas para exportar com os filtros aplicados.',
          status: 'warning',
          duration: 5000,
          isClosable: true,
        });
        return;
      }

      // Criar cabeçalhos do CSV para Simples Nacional
      const headers = [
        'ID',
        'Código',
        'CNPJ',
        'Razão Social',
        'Cidade',
        'UF',
        'Regime Tributário',
        'Faturamento',
        'Simples Nacional',
        'Saída',
        'Serviço',
        'Outros',
        'Anexo',
        'Seção',
        'Tabela',
      ];

      // Criar linhas do CSV
      const rows = data.map((item: any) => {
        const empresa = item.TbEmpresa || {};
        return [
          empresa.id || '',
          empresa.external_id || '',
          empresa.cnpj || '',
          `"${(empresa.razao_social || '').replace(/"/g, '""')}"`,
          empresa.cidade || '',
          empresa.uf || '',
          empresa.regime_tributario_atualizado || '',
          item.total_faturamento || 0,
          item.vl_simples_nacional || 0,
          item.vl_saida || 0,
          item.vl_servico || 0,
          item.vl_outros || 0,
          item.anexo_simples_nacional || '',
          item.secao_simples_nacional || '',
          item.tabela_simples_nacional || '',
        ].join(';');
      });

      // Montar CSV completo
      const csvContent = [headers.join(';'), ...rows].join('\n');
      const BOM = '\uFEFF';
      const blob = new Blob([BOM + csvContent], { type: 'text/csv;charset=utf-8;' });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute(
        'download',
        `empresas-simples-nacional-${new Date().toISOString().slice(0, 10)}.csv`,
      );
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);

      toast({
        title: 'Exportação concluída',
        description: `${data.length} empresas exportadas com sucesso.`,
        status: 'success',
        duration: 3000,
        isClosable: true,
      });
    } catch (error) {
      toast({
        title: 'Erro ao exportar',
        description: 'Ocorreu um erro ao exportar os dados. Tente novamente.',
        status: 'error',
        duration: 5000,
        isClosable: true,
      });
    } finally {
      setIsExporting(false);
    }
  };

  return (
    <div className="flex items-center justify-between pb-5">
      <div className="flex content-start lg:content-center">
        <ReturnButton previousPage="modulesHome/supervisor" />
        <div className="h-full">
          <div
            className="flex items-center justify-center rounded-full p-3"
            style={{ backgroundColor: iconBgColor }}
          >
            <TbPercentage color={iconColor} className="size-8" />
          </div>
        </div>
        <div className="flex w-full flex-col justify-center gap-1 pl-2">
          <Text fontWeight="bold" fontSize="16px">
            {title}
          </Text>
          <Text fontSize="12px" color="gray.500">
            {subtitle}
          </Text>
        </div>
      </div>

      {onExportCSV && filters && (
        <Tooltip label="Exportar planilha" hasArrow placement="left">
          <IconButton
            aria-label="Exportar planilha"
            icon={<FileSpreadsheet size={25} />}
            size="sm"
            variant="ghost"
            colorScheme="gray"
            onClick={handleExportCSV}
            isLoading={isExporting}
            bg="gray.100"
            _hover={{
              bg: 'gray.300',
              transform: 'scale(1.05)',
            }}
            transition="all 0.2s"
          />
        </Tooltip>
      )}
    </div>
  );
}
